import type { BoxSlotTemplateApiGateway } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateApiGateway"
import {
  type BoxSlotTemplateDto,
  BoxSlotTemplateDtoSchema,
  type BoxSlotTemplateWeekDto,
  BoxSlotTemplateWeekDtoSchema,
  type SlotTemplateRequestDto,
} from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateDto"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"

export class BoxSlotTemplateApi implements BoxSlotTemplateApiGateway {
  private baseUrl = "/box/programs"

  private buildBoxSessionUrl = (programId: string) => `${this.baseUrl}/${programId}/slot-templates`
  private buildBoxSlotListUrl = (programId: string) => `${this.baseUrl}/${programId}/slot-templates/add-slots`

  constructor(private api: ApiClient) {}

  async getBoxSlotTemplate(boxProgramId: string, slotTemplateId: string): Promise<BoxSlotTemplateDto> {
    const resultDto = await this.api.get<BoxSlotTemplateDto>(
      `${this.buildBoxSessionUrl(boxProgramId)}/${slotTemplateId}`,
    )
    return LoggerService.parseApiDto(BoxSlotTemplateDtoSchema, resultDto.data)
  }

  async createBoxSlotTemplate(
    boxProgramId: string,
    boxSlotTemplateRequest: SlotTemplateRequestDto,
  ): Promise<BoxSlotTemplateDto> {
    const resultDto = await this.api.post<BoxSlotTemplateDto>(
      `${this.buildBoxSessionUrl(boxProgramId)}`,
      boxSlotTemplateRequest,
    )
    return LoggerService.parseApiDto(BoxSlotTemplateDtoSchema, resultDto.data)
  }

  async createBoxSlotsTemplateList(
    boxProgramId: string,
    boxSlotTemplatesRequest: SlotTemplateRequestDto[],
  ): Promise<BoxSlotTemplateDto[]> {
    const resultDto = await this.api.post<BoxSlotTemplateDto[]>(this.buildBoxSlotListUrl(boxProgramId), {
      slotsTemplate: boxSlotTemplatesRequest,
    })
    return resultDto.data.map((boxSlotTemplateDto) =>
      LoggerService.parseApiDto(BoxSlotTemplateDtoSchema, boxSlotTemplateDto),
    )
  }

  async updateBoxSlotTemplate(
    boxProgramId: string,
    id: string,
    boxSlotTemplateRequest: SlotTemplateRequestDto,
  ): Promise<BoxSlotTemplateDto> {
    const resultDto = await this.api.put<BoxSlotTemplateDto>(
      `${this.buildBoxSessionUrl(boxProgramId)}/${id}`,
      boxSlotTemplateRequest,
    )
    return LoggerService.parseApiDto(BoxSlotTemplateDtoSchema, resultDto.data)
  }

  async deleteBoxSlotTemplate(
    boxProgramId: string,
    slotTemplateId: string,
    impactAlreadyCreatedSlots: boolean,
  ): Promise<void> {
    await this.api.delete<BoxSlotTemplateDto>(`${this.buildBoxSessionUrl(boxProgramId)}/${slotTemplateId}`, {
      params: { impactAlreadyCreatedSlots },
    })
  }

  async getAllBoxSlotsTemplates(): Promise<BoxSlotTemplateWeekDto> {
    const resultDto = await this.api.get<BoxSlotTemplateWeekDto>(`${this.baseUrl}/all-slot-templates`)
    return LoggerService.parseApiDto(BoxSlotTemplateWeekDtoSchema, resultDto.data)
  }

  async getBoxSlotsTemplates(boxProgramId: string): Promise<BoxSlotTemplateWeekDto> {
    const resultDto = await this.api.get<BoxSlotTemplateWeekDto>(`${this.buildBoxSessionUrl(boxProgramId)}`)
    return LoggerService.parseApiDto(BoxSlotTemplateWeekDtoSchema, resultDto.data)
  }
}
